import b from 'b_';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import React, { Component, Fragment } from 'react';
import ReactModal from 'react-modal';
import { translate } from 'react-i18next';
import BrowserStore from '../../browser/stores/BrowserStore';
import DebugStore from '../../browser/stores/DebugStore';
import ClearCookiesStore from '../../browser/stores/ClearCookiesStore';
import ResearchStore from '../../browser/stores/ResearchStore';
import makeElementId from '../../makeElementId';
import EnvStore from '../../stores/EnvStore';
import LS, { LS_KEY } from '../../LocalStorage';
import { AGENT_BASE } from '../../API';
import FuseButton from '../FuseControls/FuseButton';
import { FTBody, FTBodyNoColor } from '../Fuse';
import { LogoProofpointWord } from '../Logo/PfptLogo';
import { buildCSSVariableString, themesStylesConfig } from '../../themesStylesConfig';
import OldTopBarMenu from './OldTopBarMenu';
import { isFuse5Enabled } from '../../fuseFeatureFlag';
import './TopBarMenu.css';

const block = b.lock('Fuse5TopBarMenu');
const MenuGroup = ({ children }) => <Fragment>
    {children}
    <div className={block('Separator')} aria-hidden="true" />
</Fragment>;

class FuseTopBarMenu extends Component {
    static propTypes = {
        t: PropTypes.func.isRequired,
        browser: BrowserStore.PropType.isRequired,
        clearCookies: ClearCookiesStore.PropType.isRequired,
        debug: DebugStore.PropTypes.isRequired,
        research: ResearchStore.PropType.isRequired,
        env: EnvStore.PropType.isRequired,
    };

    elementId = makeElementId('Fuse5TopBarMenu');
    handleClick = (action) => (e) => {
        this.handleClose();
        action?.(e);
    };

    onDebugPointerClick = () => {
        LS.set(LS_KEY.debugPointerEvents, !LS.get(LS_KEY.debugPointerEvents, false));
        window.location.reload();
    };

    handleClose = () => {
        this.props.browser.topBarMenu.close();
    };

    renderFuseMenuItem = ({ type, icon, noIcon, children, disabled, rIcon, dots, ...props }) => {
        const { t } = this.props;

        return <FuseButton
            icon={!noIcon ? (icon || type) : null}
            iconRight={rIcon ? 'keyboard_arrow_right_rounded' : null}
            className={block('Item', { type })}
            wrapperClassName={block('BtnWrapper')}
            iconClassName={block('BtnIcon')}
            iconRightClassName={block('BtnEndIcon')}
            fullWidth
            noCenter
            disabled={disabled}
            aria-disabled={disabled}
            role="menuitem"
            {...props}
        >
            <FTBodyNoColor>
                {children || t(`TopBarMenu.${type}`)}
                {dots ? '...' : null}
            </FTBodyNoColor>
        </FuseButton>;
    };

    renderResearchMenuItems = () => {
        const { t, env, research } = this.props;
        const displayResearch = research.researchMode.isEnabled && (env.screen.isTablet || env.screen.isMobile);

        if (!displayResearch) return null;

        const userAgentInfo = research.getUserAgentInfo(research.userAgent);
        const FuseMenuItem = this.renderFuseMenuItem;

        return (
            <>
                {env.screen.isMobile && research.locations.length > 1 ? (
                    <FuseMenuItem
                        type="location"
                        icon="edit_location_rounded"
                        onClick={this.handleClick(research.locationMenu.open)}
                        dots
                    >{t('locationMenu.title')}
                    </FuseMenuItem>
                ) : null}
                {env.screen.isMobile ? (
                    <FuseMenuItem
                        type="userAgent"
                        onClick={this.handleClick(research.userAgentMenu.open)}
                        icon={userAgentInfo.os ? 'os_' + userAgentInfo.os.toLowerCase() : 'phonelink_setup'}
                        dots
                    >
                        {userAgentInfo.browser ?
                            t(`userAgentMenu.${userAgentInfo.browser}`) : t('userAgentMenu.barTitle')}
                    </FuseMenuItem>
                ) : null}
            </>
        );
    };

    renderMainMenuItems = () => {
        const { env } = this.props;
        const shouldDisplay = !(env.isUrlIsolation || env.isSaasIsolation);
        const FuseMenuItem = this.renderFuseMenuItem;

        return (
            <>
                {env.screen.isMobile && shouldDisplay ? (
                    <FuseMenuItem
                        type="newTab"
                        icon="add"
                        href="/browser"
                        target="_blank"
                        rel="noopener noreferrer"
                        onClick={this.handleClose}
                    />
                ) : null}
            </>
        );
    };

    renderSettingsMenuItems = () => {
        const { t, env, browser } = this.props;
        const shouldDisplay = !(env.isUrlIsolation || env.isSaasIsolation);
        const FuseMenuItem = this.renderFuseMenuItem;

        return (
            <>
                <FuseMenuItem
                    type="browsingRestrictions"
                    icon="warning"
                    onClick={this.handleClick(browser.browsingRestrictionsModal.open)}
                    rIcon
                />
                <FuseMenuItem
                    type="blockedFiles"
                    icon="block"
                    onClick={this.handleClick(browser.blockedFilesModal.open)}
                    rIcon
                />
                {shouldDisplay ? (
                    <FuseMenuItem
                        type="settings"
                        icon="settings"
                        onClick={this.handleClick(browser.settingsModal.open)}
                        rIcon
                    />
                ) : null}
                {browser.feedbackEnabled ? (
                    <FuseMenuItem
                        type="feedback"
                        onClick={this.handleClick(browser.sendFeedbackStore.openProductFeedbackModal)}
                        icon="feedback"
                        rIcon
                        dots
                    >{t('TopBarMenu.feedback')}</FuseMenuItem>
                ) : null}
                {browser.faqEnabled ? (
                    <FuseMenuItem
                        type="faq"
                        icon="help_round"
                        onClick={this.handleClick(browser.openFaq)}
                        rIcon
                        dots
                    />
                ) : null}
            </>
        );
    };

    renderUtilityMenuItems = () => {
        const { browser, clearCookies } = this.props;
        const FuseMenuItem = this.renderFuseMenuItem;

        return (
            <>
                {browser.clearCookiesEnabled ? (
                    <FuseMenuItem
                        type="cookies"
                        icon="remove_round"
                        onClick={this.handleClick(clearCookies.modal.open)}
                        dots
                    />
                ) : null}
                {browser.printEnabled ? (
                    <FuseMenuItem
                        type="print"
                        onClick={browser.printPage}
                        disabled={!browser.hasPage}
                        dots
                    />
                ) : null}
            </>
        );
    };

    renderConsoleLink = () => {
        const { browser, env } = this.props;
        const FuseMenuItem = this.renderFuseMenuItem;

        if (!env.mightBeCustomerAdmin || env.isUrlIsolation || env.isSaasIsolation) return null;

        return <FuseMenuItem
            type="console"
            icon="open_in_browser"
            href="/console"
            target="_blank"
            rel="noopener noreferrer"
            onClick={browser.topBarMenu.close}
        />;
    };

    renderDeveloperMenuItems = () => {
        const { browser, debug } = this.props;
        const FuseMenuItem = this.renderFuseMenuItem;

        if (!browser.isDeveloper) return null;

        return (
            <>
                <FuseMenuItem
                    type="screenshot"
                    icon="wallpaper"
                    onClick={() => window._.getScreenshot(null, true)}
                    disabled={!browser.hasPage}
                >[DEV] Screenshot</FuseMenuItem>

                <FuseMenuItem
                    type="devtools"
                    icon="build"
                    onClick={debug.openDevTools}
                >[DEV] DevTools</FuseMenuItem>

                <FuseMenuItem
                    type="debug_pointer"
                    icon={LS.get(LS_KEY.debugPointerEvents, false) ? 'toggle_on' : 'toggle_off'}
                    onClick={this.onDebugPointerClick}
                >[DEV] Toggle pointer debug</FuseMenuItem>

                <FuseMenuItem
                    type="debug_info"
                    icon="info_outline"
                    onClick={() => window.open(AGENT_BASE + '/debug-info', '_blank')}
                >[DEV] Debug Info</FuseMenuItem>
            </>
        );
    };

    renderModalContent = () => {
        return (
            <div className={block('ContentWrapper')}>
                <MenuGroup>
                    {this.renderResearchMenuItems()}
                    {this.renderMainMenuItems()}
                </MenuGroup>

                <MenuGroup>
                    {this.renderSettingsMenuItems()}
                </MenuGroup>

                <MenuGroup>
                    {this.renderUtilityMenuItems()}
                </MenuGroup>

                <MenuGroup>
                    {this.renderConsoleLink()}
                    {this.renderDeveloperMenuItems()}
                </MenuGroup>

            </div>
        );
    };

    render() {
        const { t, env, browser } = this.props;

        return <ReactModal
            id={this.elementId}
            aria-hidden={!browser.topBarMenu.isOpen}
            aria-label={t('browserTopBar.menuButtonTitle')}
            role="menu"
            className={block()}
            overlayClassName={block('Overlay', {
                troubleshootingMode: browser.troubleshootingMode,
                alternativeMobile: env.screen.isMobile && browser.isTopBarMinimizable(),
            })}
            isOpen={browser.topBarMenu.isOpen}
            onClose={this.handleClose}
            onRequestClose={this.handleClose}
            closeTimeoutMS={200}
            shouldCloseOnEsc
            shouldFocusAfterRender
            shouldReturnFocusAfterClose
        >
            <div className={block('Header')}>
                <LogoProofpointWord
                    mainColor={buildCSSVariableString(themesStylesConfig.lightTheme.typographyLink)}
                    className={block('Logo')}
                />
                <FTBody>Isolation</FTBody>
                <FuseButton
                    className={block('Close')}
                    icon="close"
                    onClick={this.handleClose}
                    qa="topbar_menu_close"
                />
            </div>
            {this.renderModalContent()}
        </ReactModal>;
    }
}

const TopBarMenu = () => {
    const ObservableTopBarMenu = translate()(inject(
        'browser',
        'clearCookies',
        'research',
        'env',
        'debug',
    )(observer(FuseTopBarMenu)));
    return isFuse5Enabled() ? <ObservableTopBarMenu/> : <OldTopBarMenu/>;
};

export default TopBarMenu;
